import { OpeningHoursSpecifications } from 'owa-location-service';
import { parseUserTime } from 'owa-datetime';
import { formatSingleDayTimeSpanWithoutDate } from 'owa-timeformat/lib/utils/formatTimeSpan';
import { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import { OwaDate } from 'owa-datetime';
import isAfter from 'owa-datetime/lib/owaDate/isAfter';
import isBefore from 'owa-datetime/lib/owaDate//isBefore';
import getLocalizedDayOfWeek from 'owa-timeformat/lib/utils/localization/getLocalizedDayOfWeek';
import DayOfWeekType from 'owa-timeformat/lib/store/schema/DayOfWeekType';
import owaDateTimeStrings from 'owa-strings/owa-common-strings';

export type WeekDays =
    | 'Sunday'
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday';

const DAYS_OF_WEEK: string[] = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];

const TIME_SEPARATOR = ':';

export interface OpeningHoursItem {
    day: WeekDays;
    times: OwaDateRange[];
}
export interface CompactOpeningHours {
    [day: string]: OwaDateRange[];
}

/**
 * Converts the opening hours received from server to OwaDateRanges aggregated by day.
 * The Server returns opening hours as an array of day, start and end in a string format Exemple:
 * [{DayOfWeek: "Tuesday", Opens: "09:00:00", Closes: "12:30:00"},{DayOfWeek: "Tuesday", Opens: "13:45:00", Closes: "17:45:00"}]
 * If a buisiness is opened multiple times slots in the same day then the openingHours will have multiple entries for that day, an entry for each time slot.
 * This function aggregates the time slots for each day and return an array of OwaDateRange indexed by the day.
 * The times from the server are strings "09:00:00" without a day. We convert them to today's date to easily compare the times to the current time.
 * For the example above,  if to day is 2/4/2019, this will return ["Tuesday": [{start: 2019-02-04T09:00, end:2019-02-04T12:30}, {start: 2019-02-04T13:45, end:2019-02-04T17:45} ]]
 */
export function getCompactOpeningHours(
    openingHours: OpeningHoursSpecifications[],
    now: OwaDate
): CompactOpeningHours {
    if (!openingHours || openingHours.length == 0) {
        return undefined;
    }

    let compactOpeningHours: CompactOpeningHours = {};
    let hours: OwaDateRange;
    openingHours.forEach(specification => {
        hours = {
            start: parseUserTime(
                specification.Opens.substring(0, specification.Opens.lastIndexOf(TIME_SEPARATOR)), // remove the seconds
                now
            ),
            end: parseUserTime(
                specification.Closes.substring(0, specification.Closes.lastIndexOf(TIME_SEPARATOR)), // remove the seconds
                now
            ),
        };
        if (compactOpeningHours[specification.DayOfWeek]) {
            compactOpeningHours[specification.DayOfWeek].push(hours);
        } else {
            compactOpeningHours[specification.DayOfWeek] = [hours];
        }
    });

    return compactOpeningHours;
}

export function getLocalizedShortDayOfWeek(day: WeekDays): string {
    return owaDateTimeStrings[day.toLocaleLowerCase() + 'AbbreviatedLowercase'];
}

export function getToday(now: OwaDate): WeekDays {
    const today = now.getDay();
    return DAYS_OF_WEEK[today] as WeekDays;
}

export function getTodaysOpeningHours(
    openingHours: CompactOpeningHours,
    now: OwaDate
): OwaDateRange[] {
    return openingHours[getToday(now)];
}

export function isOpenNow(openingHours: CompactOpeningHours, now: OwaDate) {
    const timeRanges = getTodaysOpeningHours(openingHours, now);
    if (timeRanges) {
        return timeRanges.some(
            timeRange => isBefore(now, timeRange.end) && isAfter(now, timeRange.start)
        );
    }

    return false;
}

export function getNextOpenTimes(
    openingHours: CompactOpeningHours,
    day: WeekDays
): OpeningHoursItem {
    let nextDay: WeekDays = null;
    let timeRanges: OwaDateRange[];

    // as long as openingHours is not empty then we are always able to find a next day
    do {
        // get Nextday
        nextDay = getNextDay(day) as WeekDays;
        timeRanges = openingHours[nextDay];
    } while (!timeRanges);

    return {
        day: nextDay,
        times: timeRanges,
    } as OpeningHoursItem;
}

export function getFormattedTimeRangeString(timeRange: OwaDateRange): string {
    return formatSingleDayTimeSpanWithoutDate(timeRange.start, timeRange.end);
}

export function getFormatedOpeningHoursDate(dayofWeek: string) {
    return getLocalizedDayOfWeek(dayofWeek as DayOfWeekType);
}

function getNextDay(day: WeekDays): string {
    const nextDayIndex = (DAYS_OF_WEEK.indexOf(day) + 1) % 7;
    return DAYS_OF_WEEK[nextDayIndex];
}
